/**
 * Icon-badged stat tile for dashboard home pages — same `.stat-tile`/
 * `.stat-grid` markup `UserListPage.tsx` already renders by hand (bare,
 * iconless, non-`<Link>` `<li className="stat-tile">`), so that page's
 * markup is untouched by this component's existence; this just centralizes
 * the icon slot plus the `<Link>`-vs-plain-`<span>` branching every dashboard
 * home page used to hand-roll per tile (see `TenantHome.tsx`'s pre-redesign
 * nullable tiles). `.stat-tile`/`.stat-grid` are Carbon-token-styled in
 * styles/carbon.scss (search "Stat tiles / dashboard panels") — UserListPage
 * inherits that restyle too, without needing a code change of its own.
 */
import { Link } from 'react-router-dom';
import type { CarbonIconType } from '@carbon/icons-react';

export interface StatTileProps {
  icon: CarbonIconType;
  value: React.ReactNode;
  label: string;
  detail?: React.ReactNode;
  /** A second detail line, rendered as its own `.stat-tile__detail` span — e.g. TenantHome's lease tile needs both "{property} · {unit}" and "Lease ends {date}" as separately-matchable text, not concatenated into one. */
  secondaryDetail?: React.ReactNode;
  /** Renders a `<Link>` when given, a plain `<span>` otherwise (e.g. a nullable tenant tile with nowhere to link). */
  to?: string;
}

export function StatTile({
  icon: Icon,
  value,
  label,
  detail,
  secondaryDetail,
  to,
}: StatTileProps): React.JSX.Element {
  const content = (
    <>
      <span className="stat-tile__icon" aria-hidden="true">
        <Icon size={20} />
      </span>
      <span className="stat-tile__value">{value}</span>
      <span className="stat-tile__label">{label}</span>
      {detail !== undefined && <span className="stat-tile__detail">{detail}</span>}
      {secondaryDetail !== undefined && <span className="stat-tile__detail">{secondaryDetail}</span>}
    </>
  );

  return to !== undefined ? (
    <Link to={to} className="stat-tile">
      {content}
    </Link>
  ) : (
    <span className="stat-tile">{content}</span>
  );
}

export default StatTile;
